// Empire Apps Chassis · connectors/financial/kite-connect
// Zerodha Kite Connect — read-only holdings + positions + orders.
//
// Spec: https://kite.trade/docs/connect/v3/
// Endpoints used: /portfolio/holdings · /portfolio/positions · /orders
// Auth: api_key + access_token (daily refresh required by Zerodha).
// Test-mode returns realistic 12-holding mock portfolio.

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type HoldingPosition,
  brokerId,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';

const DEV = true;
const STATUS_KEY = 'empire:financial:kite:status';
const KITE_BASE = 'https://api.kite.trade';

interface PersistedStatus {
  readonly connected: boolean;
  readonly userId: string | null;
  readonly lastSync: string | null;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, userId: null, lastSync: null });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

interface KiteHoldingRow {
  readonly tradingsymbol: string;
  readonly exchange: 'NSE' | 'BSE';
  readonly quantity: number;
  readonly average_price: number;
  readonly last_price: number;
}

interface KiteResponse<T> {
  readonly status: 'success' | 'error';
  readonly data: T;
}

async function fetchKiteHoldings(apiKey: string, accessToken: string): Promise<readonly KiteHoldingRow[]> {
  const r = await fetch(`${KITE_BASE}/portfolio/holdings`, {
    headers: {
      'X-Kite-Version': '3',
      Authorization: `token ${apiKey}:${accessToken}`,
    },
  });
  if (!r.ok) throw new Error(`Kite ${r.status}`);
  const j = (await r.json()) as KiteResponse<readonly KiteHoldingRow[]>;
  if (j.status !== 'success') throw new Error('Kite returned non-success.');
  return j.data;
}

function mockHoldings(): readonly HoldingPosition[] {
  const seed: ReadonlyArray<readonly [string, 'NSE' | 'BSE', number, number, number]> = [
    ['RELIANCE', 'NSE', 25, 245000, 295000],
    ['HDFCBANK', 'NSE', 40, 152000, 167500],
    ['INFY', 'NSE', 60, 142000, 178000],
    ['TCS', 'NSE', 15, 365000, 398000],
    ['ITC', 'NSE', 200, 41200, 47800],
    ['BAJFINANCE', 'NSE', 8, 645000, 702500],
    ['ASIANPAINT', 'NSE', 20, 295000, 268000],
    ['HINDUNILVR', 'NSE', 30, 252000, 247000],
    ['SBIN', 'NSE', 120, 58200, 78400],
    ['LT', 'NSE', 18, 285000, 364000],
    ['ADANIPORTS', 'NSE', 50, 78400, 132500],
    ['DIVISLAB', 'NSE', 12, 345000, 524000],
  ];
  return seed.map(([sym, ex, qty, avg, ltp]) =>
    Object.freeze<HoldingPosition>({
      brokerId: brokerId('zerodha'),
      symbol: sym,
      exchange: ex,
      quantity: qty,
      avgCostPaise: paise(avg),
      ltpPaise: paise(ltp),
      asOf: isoTs(),
    }),
  );
}

export const kiteConnect: Connector = {
  id: 'kite-connect',
  displayName: 'Zerodha Kite Connect',
  category: 'broker',
  defaultMode: DEV ? 'mock' : 'live',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: DEV ? 'mock' : 'live',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.connected ? 1 : 0,
      disabledReason: DEV ? null : 'Connect-app id pending Zerodha approval.',
    };
  },

  async connect(ctx, opts): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    const userId = opts?.userId ?? 'ZX5832';
    logConnectorConsent(ctx.dpdpLog, 'connector-broker-read', 'zerodha/holdings+positions+orders');
    const connectedAt = isoTs();
    writeStatus({ connected: true, userId, lastSync: connectedAt });
    return ok({ connectedAt });
  },

  async import(_ctx, opts): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected to Kite.', true);
    if (!DEV && opts?.fileBlob === undefined) {
      try {
        const apiKey = '';
        const accessToken = '';
        if (!apiKey || !accessToken) return fail('Kite api_key + access_token not configured.', false);
        const rows = await fetchKiteHoldings(apiKey, accessToken);
        const holdings: readonly HoldingPosition[] = rows.map((r) =>
          Object.freeze<HoldingPosition>({
            brokerId: brokerId('zerodha'),
            symbol: r.tradingsymbol,
            exchange: r.exchange,
            quantity: r.quantity,
            avgCostPaise: paise(r.average_price * 100),
            ltpPaise: paise(r.last_price * 100),
            asOf: isoTs(),
          }),
        );
        writeStatus({ ...s, lastSync: isoTs() });
        return ok({ holdings });
      } catch (e) {
        return fail(`Kite fetch failed: ${(e as Error).message}`, true);
      }
    }
    const holdings = mockHoldings();
    writeStatus({ ...s, lastSync: isoTs() });
    return ok({ holdings });
  },

  async disconnect(ctx): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, userId: null, lastSync: null });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-broker-read', broker: 'zerodha' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { holdings: mockHoldings() };
  },
};
